import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthService } from '../services/authService';
import { ClientInfo, Session } from '../types/auth';
import { useTranslation } from '../hooks/useTranslation';
import toast from 'react-hot-toast';

interface CheckinParams {
  clientUuid: string;
  timestamp: string;
}

const CheckinPage: React.FC = () => {
  const { clientUuid, timestamp } = useParams<keyof CheckinParams>() as CheckinParams;
  const navigate = useNavigate();
  const { isAuthenticated, user, login } = useAuth();
  const { t } = useTranslation();
  
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [sessionInfo, setSessionInfo] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState<string>('');

  // Timer effect for active sessions
  useEffect(() => {
    if (sessionStartTime) {
      const interval = setInterval(() => {
        const now = new Date();
        const elapsed = now.getTime() - sessionStartTime.getTime();
        const hours = Math.floor(elapsed / (1000 * 60 * 60));
        const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
        setElapsedTime(`${hours}h ${minutes}m`);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [sessionStartTime]);

  // Load client info and check session status
  useEffect(() => {
    const loadCheckinData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!clientUuid || !timestamp) {
          setError(t('qrCodeInvalid'));
          return;
        }

        // Validate timestamp (QR codes should be recent)
        const qrTimestamp = parseInt(timestamp);
        const now = Date.now();
        const maxAge = 5 * 60 * 1000; // 5 minutes
        if (now - qrTimestamp > maxAge) {
          setError(t('qrCodeExpired'));
          return;
        }

        // Get client information
        const clientResponse = await AuthService.getClientInfo(clientUuid);
        if (!clientResponse.success) {
          setError(t('clientNotFound'));
          return;
        }

        setClientInfo(clientResponse.data?.clientInfo || null);

        // If authenticated, check for existing session
        if (isAuthenticated && user) {
          const sessionResponse = await AuthService.getActiveSession();
          if (sessionResponse.success && sessionResponse.data?.activeSession) {
            setSessionInfo(sessionResponse.data.activeSession);
            setSessionStartTime(new Date(sessionResponse.data.activeSession.checkInTime));
          }
        }

      } catch (err) {
        console.error('Error loading checkin data:', err);
        setError(t('errorLoadingInfo'));
      } finally {
        setIsLoading(false);
      }
    };

    loadCheckinData();
  }, [clientUuid, timestamp, isAuthenticated, user, t]);

  const handleCheckIn = async () => {
    if (!clientInfo || !user) return;

    try {
      setIsCheckingIn(true);
      
      const response = await AuthService.startSession({
        clientId: clientInfo.userId,
        timestamp: Date.now().toString()
      });

      if (response.success && response.data?.activeSession) {
        setSessionInfo(response.data.activeSession);
        setSessionStartTime(new Date());
        toast.success(t('sessionStartedSuccessfully'));
      } else {
        console.error('Start session failed:', response);
        toast.error(response.error || response.message || t('errorStartingSession'));
      }
    } catch (err) {
      console.error('Error starting session:', err);
      const errorMessage = err instanceof Error ? err.message : t('networkError');
      toast.error(`${t('error')} ${errorMessage}`);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleLogin = () => {
    navigate('/login', { 
      state: { 
        redirectTo: `/checkin/${clientUuid}/${timestamp}`,
        clientInfo 
      } 
    });
  };

  const handleRegister = () => {
    navigate('/register', { 
      state: { 
        redirectTo: `/checkin/${clientUuid}/${timestamp}`,
        clientInfo 
      } 
    });
  };

  const handleWhatsAppSupport = () => {
    const message = encodeURIComponent(t('whatsAppSupportMessage'));
    window.open(`https://wa.me/573236344006?text=${message}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('qrCodeInvalid')}</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('backToHome')}
          </button>
        </div>
        
        {/* Floating WhatsApp Support */}
        <button
          onClick={handleWhatsAppSupport}
          className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors"
          title={t('whatsAppSupport')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
        </button>
      </div>
    );
  }

  // Provider has an active session
  if (sessionInfo && sessionStartTime) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-green-500 text-6xl mb-4">⏱️</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('activeSession')}</h1>
          
          <div className="bg-white rounded-lg p-6 mb-6 shadow-lg">
            <div className="text-4xl font-bold text-green-600 mb-2">{elapsedTime}</div>
            <p className="text-gray-600 mb-4">{t('elapsedTime')}</p>
            
            {clientInfo && (
              <div className="border-t pt-4">
                <div className="flex items-center justify-center mb-3">
                  {clientInfo.photo ? (
                    <img 
                      src={clientInfo.photo} 
                      alt={clientInfo.name}
                      className="w-12 h-12 rounded-full mr-3"
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-300 rounded-full mr-3 flex items-center justify-center">
                      <span className="text-gray-600 font-semibold">
                        {clientInfo.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="text-left">
                    <p className="font-semibold text-gray-800">{clientInfo.name}</p>
                    <p className="text-sm text-gray-600">{t('client')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-blue-800 text-sm">
              {t('remindClient')}
            </p>
          </div>
        </div>
        
        {/* Floating WhatsApp Support */}
        <button
          onClick={handleWhatsAppSupport}
          className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors"
          title={t('whatsAppSupport')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
        </button>
      </div>
    );
  }

  // Authenticated provider ready to check in
  if (isAuthenticated && user && clientInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-blue-500 text-6xl mb-4">👋</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('confirmCheckIn')}</h1>
          
          <div className="bg-white rounded-lg p-6 mb-6 shadow-lg">
            <div className="flex items-center justify-center mb-4">
              {clientInfo.photo ? (
                <img 
                  src={clientInfo.photo} 
                  alt={clientInfo.name}
                  className="w-16 h-16 rounded-full mr-4"
                />
              ) : (
                <div className="w-16 h-16 bg-gray-300 rounded-full mr-4 flex items-center justify-center">
                  <span className="text-gray-600 font-semibold text-xl">
                    {clientInfo.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="text-left">
                <p className="font-semibold text-gray-800 text-lg">{clientInfo.name}</p>
                <p className="text-sm text-gray-600">{t('client')}</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              {t('readyToStartSession')}
            </p>
            
            <button
              onClick={handleCheckIn}
              disabled={isCheckingIn}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isCheckingIn ? t('starting') : t('startSession')}
            </button>
          </div>
        </div>
        
        {/* Floating WhatsApp Support */}
        <button
          onClick={handleWhatsAppSupport}
          className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors"
          title={t('whatsAppSupport')}
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
          </svg>
        </button>
      </div>
    );
  }

  // Unauthenticated provider
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="text-purple-500 text-6xl mb-4">📱</div>
        <h1 className="text-2xl font-bold text-gray-800 mb-4">{t('checkInProvider')}</h1>
        
        {/* Hero Video Placeholder */}
        <div className="bg-gray-200 rounded-lg h-48 mb-6 flex items-center justify-center">
          <div className="text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
            <p>{t('heroVideoPlaceholder')}</p>
          </div>
        </div>
        
        {/* Client Information */}
        {clientInfo && (
          <div className="bg-white rounded-lg p-6 mb-6 shadow-lg">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">{t('clientInformation')}</h2>
            <div className="flex items-center justify-center mb-3">
              {clientInfo.photo ? (
                <img 
                  src={clientInfo.photo} 
                  alt={clientInfo.name}
                  className="w-12 h-12 rounded-full mr-3"
                />
              ) : (
                <div className="w-12 h-12 bg-gray-300 rounded-full mr-3 flex items-center justify-center">
                  <span className="text-gray-600 font-semibold">
                    {clientInfo.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="text-left">
                <p className="font-semibold text-gray-800">{clientInfo.name}</p>
                <p className="text-sm text-gray-600">{t('client')}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Marketing Content Placeholder */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-blue-800 text-sm">
            {t('joinSimpleHandshake')}
          </p>
        </div>
        
        {/* Authentication Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleLogin}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('login')}
          </button>
          <button
            onClick={handleRegister}
            className="w-full bg-green-600 text-white py-3 px-6 rounded-lg hover:bg-green-700 transition-colors"
          >
            {t('register')}
          </button>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>{t('alreadyHaveAccount')} <button onClick={handleLogin} className="text-blue-600 hover:underline">{t('login')}</button></p>
          <p>{t('newUser')} <button onClick={handleRegister} className="text-green-600 hover:underline">{t('register')}</button></p>
        </div>
      </div>
      
      {/* Floating WhatsApp Support */}
      <button
        onClick={handleWhatsAppSupport}
        className="fixed bottom-6 right-6 bg-green-500 text-white p-4 rounded-full shadow-lg hover:bg-green-600 transition-colors"
        title={t('whatsAppSupport')}
      >
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
        </svg>
      </button>
    </div>
  );
};

export default CheckinPage; 